<template>
  <div class="category-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Categorie laden...</p>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">
        <i class="bi bi-exclamation-triangle"></i>
      </div>
      <h3>Categorie niet gevonden</h3>
      <p>De categorie die je zoekt bestaat niet of is niet beschikbaar.</p>
      <router-link to="/producten" class="btn-back">
        <i class="bi bi-arrow-left"></i>
        Terug naar producten
      </router-link>
    </div>
    
    <!-- Category Content -->
    <div v-else-if="category" class="category-content">
      
      <!-- Breadcrumb -->
      <section class="breadcrumb-section">
        <div class="container">
          <nav class="breadcrumb-nav">
            <router-link to="/" class="breadcrumb-link">Home</router-link>
            <i class="bi bi-chevron-right"></i>
            <router-link to="/producten" class="breadcrumb-link">Producten</router-link>
            <i class="bi bi-chevron-right"></i>
            <template v-if="parentCategory">
              <router-link :to="`/categorie/${parentCategory.slug}`" class="breadcrumb-link">{{ parentCategory.name }}</router-link>
              <i class="bi bi-chevron-right"></i>
            </template>
            <span class="breadcrumb-current">{{ category.name }}</span>
          </nav>
        </div>
      </section>
      
      <!-- Category Hero -->
      <section class="category-hero" :class="{ 'hero-landing': isLandingPage }">
        <div class="hero-overlay"></div>
        <div v-if="category.image" class="hero-background">
          <img :src="category.image.src" :alt="category.name">
        </div>
        <div class="container">
          <div class="hero-content">
            <div class="category-badge">
              <i :class="getCategoryIcon(category.slug)"></i>
              <span>{{ getCategoryType(category.slug) }}</span>
            </div>
            <h1 class="category-title">{{ category.name }}</h1>
            <p v-if="category.description" class="category-subtitle" v-html="getExcerpt(category.description, 150)"></p>

            <!-- CTA knop (geïnspireerd op denotenshop) -->
            <div v-show="!isLandingPage && category.count > 0" class="hero-cta-row">
              <a href="#products-section" class="hero-cta-btn">
                <i class="bi bi-grid-3x3-gap"></i>
                Bekijk alle {{ category.count }} {{ category.name.toLowerCase() }}
              </a>
            </div>

            <!-- Stats Bar -->
            <div class="category-stats">
              <div class="stat-item">
                <div class="stat-icon">
                  <i class="bi bi-box-seam"></i>
                </div>
                <div>
                  <div class="stat-number">{{ category.count }}</div>
                  <div class="stat-label">Producten</div>
                </div>
              </div>
              <div v-if="hasSubcategories" class="stat-item">
                <div class="stat-icon">
                  <i class="bi bi-layers"></i>
                </div>
                <div>
                  <div class="stat-number">{{ subcategories.length }}</div>
                  <div class="stat-label">Subcategorieën</div>
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-icon">
                  <i class="bi bi-truck"></i>
                </div>
                <div>
                  <div class="stat-number">Gratis</div>
                  <div class="stat-label">Verzending</div>
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-icon">
                  <i class="bi bi-shield-check"></i>
                </div>
                <div>
                  <div class="stat-number">2 jaar</div>
                  <div class="stat-label">Garantie</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Subcategorieën - visuele kaartjes (denotenshop-stijl) -->
      <section v-if="hasSubcategories && !isLandingPage" class="subcat-cards-section">
        <div class="container">
          <h2 class="subcat-cards-title">
            <i class="bi bi-grid-3x3-gap"></i>
            Subcategorieën
          </h2>
          <div class="subcat-cards-grid">
            <router-link
              v-for="subcat in subcategories"
              :key="subcat.id"
              :to="`/categorie/${subcat.slug}`"
              class="subcat-card"
            >
              <!-- Afbeelding of icon -->
              <div class="subcat-card-media">
                <img
                  v-if="subcat.image"
                  :src="subcat.image.src"
                  :alt="subcat.name"
                  class="subcat-card-img"
                  loading="lazy"
                >
                <div v-else class="subcat-card-icon">
                  <i :class="getCategoryIcon(subcat.slug)"></i>
                </div>
              </div>
              <!-- Body -->
              <div class="subcat-card-body">
                <span class="subcat-card-name">{{ subcat.name }}</span>
                <span class="subcat-card-count">{{ subcat.count || 0 }} producten</span>
              </div>
              <i class="bi bi-arrow-right subcat-card-arrow"></i>
            </router-link>
          </div>
        </div>
      </section>
      
      <!-- ══════════════════════════════════════════════════════════════════════ -->
      <!-- LANDING PAGE - parent-categorie met subcategorieën                    -->
      <!-- ══════════════════════════════════════════════════════════════════════ -->
      <template v-if="isLandingPage">

        <!-- Trust-strip (hairline) -->
        <section class="landing-intro">
          <div class="container">
            <div class="landing-trust-strip">
              <span><i class="bi bi-truck"></i> Gratis verzending v.a. €50</span>
              <span><i class="bi bi-shield-check"></i> 2 jaar garantie</span>
              <span><i class="bi bi-headset"></i> Technisch advies ma–vr</span>
              <span><i class="bi bi-arrow-clockwise"></i> 30 dagen retour</span>
            </div>
          </div>
        </section>

        <!-- Subcategorie kaartgrid (hoofd-navigatie) -->
        <section class="landing-grid-section">
          <div class="container">
            <header class="landing-grid-head">
              <h2 class="landing-grid-title">Kies een categorie</h2>
              <p class="landing-grid-sub">Selecteer een subcategorie om het volledige aanbod te bekijken.</p>
            </header>
            <div class="landing-grid">
              <router-link
                v-for="subcat in subcategories"
                :key="subcat.id"
                :to="`/categorie/${subcat.slug}`"
                class="landing-card"
              >
                <div class="landing-card-media">
                  <img
                    v-if="subcat.image"
                    :src="subcat.image.src"
                    :alt="subcat.name"
                    class="landing-card-img"
                    loading="lazy"
                  >
                  <div v-else class="landing-card-icon">
                    <i :class="getCategoryIcon(subcat.slug)"></i>
                  </div>
                </div>

                <div class="landing-card-body">
                  <span class="landing-card-meta">{{ subcat.count || 0 }} producten</span>
                  <h3 class="landing-card-title">{{ subcat.name }}</h3>
                  <p v-if="subcat.description" class="landing-card-desc">{{ getExcerpt(subcat.description, 80) }}</p>
                  <span class="landing-card-cta">Bekijk aanbod <i class="bi bi-arrow-right"></i></span>
                </div>
              </router-link>
            </div>
          </div>
        </section>

        <!-- SEO-informatieblok -->
        <section class="landing-seo-section">
          <div class="container">
            <div class="landing-seo-content">
              <h2 class="landing-seo-title">Over {{ category.name }}</h2>
              <div v-if="category.description" class="seo-content landing-seo-body" :class="{ 'is-plaintext': descriptionIsPlain }" v-html="category.description"></div>
              <p v-else class="landing-seo-body">
                In deze categorie vindt u een compleet assortiment {{ category.name.toLowerCase() }}-producten,
                geselecteerd op kwaliteit, duurzaamheid en installeerbaarheid door vakmensen.
                Of u nu op zoek bent naar instapmodellen of professionele high-end oplossingen:
                Plug2Charge levert uit eigen voorraad met technische ondersteuning.
              </p>
            </div>
          </div>
        </section>

      </template>

      <!-- ══════════════════════════════════════════════════════════════════════ -->
      <!-- PRODUCT GRID - leaf-categorie zonder subcategorieën                   -->
      <!-- ══════════════════════════════════════════════════════════════════════ -->

      <!-- Filters & Products Section -->
      <section v-if="!isLandingPage" id="products-section" class="products-section">
        <div class="container">

          <!-- Keuzehulp-instap -->
          <router-link to="/keuzehulp" class="keuzehulp-cta">
            <span class="kh-icon"><i class="bi bi-compass"></i></span>
            <span class="kh-text">
              <strong>Moeite met kiezen?</strong>
              <span>Doe de keuzehulp en vind snel het juiste product.</span>
            </span>
            <span class="kh-arrow">Start <i class="bi bi-arrow-right"></i></span>
          </router-link>

          <!-- Filters Top Bar -->
          <div class="filters-topbar">
            <div class="topbar-left">
              <button 
                @click="showFiltersPanel = !showFiltersPanel"
                class="btn-filters"
                :class="{ active: showFiltersPanel }"
              >
                <i class="bi bi-funnel"></i>
                <span>Filters</span>
                <span v-if="activeFiltersCount > 0" class="filter-badge">{{ activeFiltersCount }}</span>
              </button>
              
              <div class="results-info">
                <strong>{{ filteredProducts.length }}</strong>
                <span>{{ filteredProducts.length === 1 ? 'product' : 'producten' }} gevonden</span>
              </div>
            </div>
            
            <div class="topbar-right">
              <div class="sort-wrapper">
                <label for="sort-select">
                  <i class="bi bi-sort-up"></i>
                  Sorteren:
                </label>
                <select 
                  id="sort-select"
                  v-model="sortBy" 
                  @change="onSortChange"
                  class="sort-select"
                >
                  <option value="menu_order">Aanbevolen</option>
                  <option value="popularity">Meest populair</option>
                  <option value="rating">Beste beoordelingen</option>
                  <option value="date">Nieuwste eerst</option>
                  <option value="price">Prijs: Laag → Hoog</option>
                  <option value="price-desc">Prijs: Hoog → Laag</option>
                </select>
              </div>
              
              <div class="view-toggle">
                <button 
                  @click="viewMode = 'grid'"
                  class="view-btn"
                  :class="{ active: viewMode === 'grid' }"
                  title="Grid weergave"
                >
                  <i class="bi bi-grid"></i>
                </button>
                <button 
                  @click="viewMode = 'list'"
                  class="view-btn"
                  :class="{ active: viewMode === 'list' }"
                  title="Lijst weergave"
                >
                  <i class="bi bi-list-ul"></i>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Active Filters Pills -->
          <div v-if="activeFiltersCount > 0" class="active-filters-bar">
            <div class="filters-pills">
              <span class="pill-label">Actieve filters:</span>
              
              <button 
                v-if="priceFilter.min || priceFilter.max" 
                @click="clearPriceFilter"
                class="filter-pill"
              >
                <i class="bi bi-currency-euro"></i>
                {{ formatPrice(priceFilter.min || 0) }} - {{ formatPrice(priceFilter.max || 9999) }}
                <i class="bi bi-x"></i>
              </button>
              
              <button 
                v-for="brand in selectedBrands" 
                :key="brand"
                @click="removeBrand(brand)"
                class="filter-pill"
              >
                <i class="bi bi-tag"></i>
                {{ brand }}
                <i class="bi bi-x"></i>
              </button>
              
              <button v-if="filters.inStock" @click="filters.inStock = false" class="filter-pill">
                <i class="bi bi-check2"></i>
                Op voorraad
                <i class="bi bi-x"></i>
              </button>
              
              <button v-if="filters.onSale" @click="filters.onSale = false" class="filter-pill">
                <i class="bi bi-percent"></i>
                Sale
                <i class="bi bi-x"></i>
              </button>
            </div>
            
            <button @click="resetFilters" class="btn-clear-all">
              <i class="bi bi-arrow-clockwise"></i>
              Wis alles
            </button>
          </div>
          
          <div class="content-grid">
            
            <!-- Filters Sidebar -->
            <aside 
              class="filters-sidebar"
              :class="{ 'is-open': showFiltersPanel }"
            >
              <div class="sidebar-header">
                <h3>
                  <i class="bi bi-funnel"></i>
                  Filters
                </h3>
                <button 
                  @click="showFiltersPanel = false"
                  class="btn-close-filters"
                >
                  <i class="bi bi-x"></i>
                </button>
              </div>
              
              <div class="sidebar-body">
                
                <!-- Price Range Filter -->
                <div class="filter-group">
                  <h4 class="filter-title">
                    <i class="bi bi-currency-euro"></i>
                    Prijs
                  </h4>
                  <div class="filter-content">
                    <div class="price-inputs">
                      <div class="price-input-wrapper">
                        <span class="currency">€</span>
                        <input 
                          v-model.number="priceFilter.min"
                          type="number" 
                          placeholder="Min"
                          class="price-input"
                        >
                      </div>
                      <span class="price-separator">t/m</span>
                      <div class="price-input-wrapper">
                        <span class="currency">€</span>
                        <input 
                          v-model.number="priceFilter.max"
                          type="number" 
                          placeholder="Max"
                          class="price-input"
                        >
                      </div>
                    </div>
                    <div class="price-range-info">
                      Bereik: {{ formatPrice(currentPriceRange.min) }} - {{ formatPrice(currentPriceRange.max) }}
                    </div>
                  </div>
                </div>
                
                <!-- Brand Filter -->
                <div v-if="availableBrands.length > 0" class="filter-group">
                  <h4 class="filter-title">
                    <i class="bi bi-tag"></i>
                    Merk
                  </h4>
                  <div class="filter-content">
                    <div class="filter-options">
                      <label
                        v-for="brand in availableBrands"
                        :key="brand"
                        class="filter-option"
                        @click.prevent="selectedBrands.includes(brand) ? removeBrand(brand) : selectedBrands.push(brand)"
                      >
                        <span class="custom-check" :class="{ checked: selectedBrands.includes(brand) }">
                          <i v-if="selectedBrands.includes(brand)" class="bi bi-check"></i>
                        </span>
                        <span class="option-label" :class="{ 'font-bold': selectedBrands.includes(brand) }">{{ brand }}</span>
                        <span class="option-count">{{ getBrandCount(brand) }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Availability Filter -->
                <div class="filter-group">
                  <h4 class="filter-title">
                    <i class="bi bi-box-seam"></i>
                    Beschikbaarheid
                  </h4>
                  <div class="filter-content">
                    <div class="filter-options">
                      <label class="filter-option" @click.prevent="filters.inStock = !filters.inStock">
                        <span class="custom-check" :class="{ checked: filters.inStock }">
                          <i v-if="filters.inStock" class="bi bi-check"></i>
                        </span>
                        <span class="option-label" :class="{ 'font-bold': filters.inStock }">Op voorraad</span>
                        <span class="stock-dot">●</span>
                      </label>
                      <label class="filter-option" @click.prevent="filters.onSale = !filters.onSale">
                        <span class="custom-check" :class="{ checked: filters.onSale }">
                          <i v-if="filters.onSale" class="bi bi-check"></i>
                        </span>
                        <span class="option-label" :class="{ 'font-bold': filters.onSale }">In de sale</span>
                        <span class="sale-dot">%</span>
                      </label>
                    </div>
                  </div>
                </div>
                
              </div>
              
              <!-- Sidebar Footer -->
              <div class="sidebar-footer">
                <button 
                  v-if="activeFiltersCount > 0"
                  @click="resetFilters"
                  class="btn-reset-filters"
                >
                  <i class="bi bi-arrow-clockwise"></i>
                  Reset filters
                </button>
                <button 
                  @click="showFiltersPanel = false"
                  class="btn-apply-filters"
                >
                  Toon {{ filteredProducts.length }} producten
                </button>
              </div>
            </aside>
            
            <!-- Products Area -->
            <div class="products-area">
              
              <!-- Products Loading - skeleton kaarten -->
              <div v-if="productsLoading" :class="viewMode === 'grid' ? 'products-grid' : 'flex flex-col gap-3'">
                <template v-if="viewMode === 'grid'">
                  <div v-for="n in 12" :key="n" class="skeleton-card">
                    <div class="skeleton-img"></div>
                    <div class="skeleton-body">
                      <div class="skeleton-line w-1/2"></div>
                      <div class="skeleton-line w-full"></div>
                      <div class="skeleton-line w-4/5"></div>
                      <div class="skeleton-line w-1/3 mt-2"></div>
                      <div class="skeleton-price"></div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div v-for="n in 8" :key="n" class="skeleton-list-card">
                    <div class="skeleton-list-img"></div>
                    <div class="skeleton-body flex-1">
                      <div class="skeleton-line w-3/5"></div>
                      <div class="skeleton-line w-full"></div>
                      <div class="skeleton-line w-2/5 mt-2"></div>
                    </div>
                    <div class="skeleton-list-btn"></div>
                  </div>
                </template>
              </div>
              
              <!-- Products Grid / List -->
              <div v-else-if="filteredProducts.length > 0">

                <!-- Rasterweergave -->
                <div v-if="viewMode === 'grid'" class="products-grid">
                  <ProductCard
                    v-for="(product, i) in paginatedProducts"
                    :key="product.id"
                    :product="product"
                    :index="i"
                  />
                </div>

                <!-- Lijstweergave -->
                <div v-else class="flex flex-col gap-3">
                  <ProductListCard
                    v-for="(product, i) in paginatedProducts"
                    :key="product.id"
                    :product="product"
                    :index="i"
                  />
                </div>

              </div>
              
              <!-- No Products State -->
              <div v-else class="no-products">
                <div class="no-products-icon">
                  <i class="bi bi-box-seam"></i>
                </div>
                <h3>Geen producten gevonden</h3>
                <p v-if="activeFiltersCount > 0">Helaas, er zijn geen producten die aan je huidige filters voldoen. Pas de filters aan of reset ze om alle producten te zien.</p>
                <p v-else>Er zijn momenteel geen producten beschikbaar in deze categorie. Bekijk ons volledige aanbod.</p>
                <div class="no-products-actions">
                  <button v-if="activeFiltersCount > 0" @click="resetFilters" class="btn-reset-large">
                    <i class="bi bi-arrow-clockwise"></i>
                    Reset alle filters
                  </button>
                  <router-link to="/producten" class="btn-all-products">
                    <i class="bi bi-grid-3x3-gap"></i>
                    Bekijk alle producten
                  </router-link>
                </div>
              </div>
              
              <!-- Pagination -->
              <div v-if="totalPages > 1" class="pagination">
                <button 
                  @click="goToPage(currentPage - 1)"
                  :disabled="currentPage === 1"
                  class="page-btn prev"
                >
                  <i class="bi bi-chevron-left"></i>
                  <span>Vorige</span>
                </button>
                
                <div class="page-numbers">
                  <button 
                    v-for="(page, index) in visiblePages" 
                    :key="index"
                    @click="goToPage(page)"
                    class="page-number"
                    :class="{ active: page === currentPage, ellipsis: page === '...' }"
                    :disabled="page === '...'"
                  >
                    {{ page }}
                  </button>
                </div>
                
                <button 
                  @click="goToPage(currentPage + 1)"
                  :disabled="currentPage === totalPages"
                  class="page-btn next"
                >
                  <span>Volgende</span>
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Trust Banner -->
      <section class="trust-banner">
        <div class="container">
          <div class="trust-content">
            <div class="trust-item">
              <div class="trust-icon"><i class="bi bi-truck"></i></div>
              <div><strong>Gratis verzending</strong><span>Vanaf €50</span></div>
            </div>
            <div class="trust-item">
              <div class="trust-icon"><i class="bi bi-shield-check"></i></div>
              <div><strong>2 jaar garantie</strong><span>Op alle producten</span></div>
            </div>
            <div class="trust-item">
              <div class="trust-icon"><i class="bi bi-headset"></i></div>
              <div><strong>Expert support</strong><span>Ma-Vr 9:00-17:00</span></div>
            </div>
            <div class="trust-item">
              <div class="trust-icon"><i class="bi bi-arrow-clockwise"></i></div>
              <div><strong>30 dagen retour</strong><span>Risico vrij</span></div>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Related Categories -->
      <section v-if="relatedCategories.length > 0" class="related-section">
        <div class="container">
          <div class="mb-4">
            <h2 class="text-[1rem] font-bold text-[#111] mb-0.5">Gerelateerde categorieën</h2>
            <p class="text-[0.8rem] text-[#aaa]">Ontdek meer producten</p>
          </div>
          <div class="related-grid">
            <router-link
              v-for="cat in relatedCategories"
              :key="cat.id"
              :to="`/categorie/${cat.slug}`"
              class="related-card"
            >
              <div class="related-icon"><i :class="getCategoryIcon(cat.slug)"></i></div>
              <div class="related-info">
                <h4>{{ cat.name }}</h4>
                <span>{{ cat.count }} producten</span>
              </div>
              <i class="bi bi-arrow-right related-arrow"></i>
            </router-link>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════════════ -->
      <!-- FAQ - categorie-specifieke vragen (verborgen op hoofd-FAQ-pagina)    -->
      <!-- ══════════════════════════════════════════════════════════════════════ -->
      <section v-if="categoryFaqs.length" class="cat-faq-section">
        <div class="container">
          <div class="cat-faq-header">
            <div class="cat-faq-icon">
              <i class="bi bi-patch-question"></i>
            </div>
            <div>
              <h2 class="cat-faq-title">Veelgestelde vragen over {{ category.name }}</h2>
              <p class="cat-faq-sub">Snel antwoord op de meest gestelde vragen in deze categorie.</p>
            </div>
          </div>

          <div class="cat-faq-groups">
            <template v-for="group in categoryFaqs" :key="group.id">
              <!-- Direct items van de groep -->
              <template v-if="group.items && group.items.length">
                <div v-if="group.name && categoryFaqs.length > 1" class="cat-faq-group-label">
                  {{ group.name }}
                </div>
                <div class="cat-faq-list">
                  <div
                    v-for="item in group.items"
                    :key="item.id"
                    class="cat-faq-item"
                    :class="{ open: openFaqItems.has(item.id) }"
                  >
                    <button class="cat-faq-q" @click="toggleFaq(item.id)">
                      <span>{{ item.question }}</span>
                      <span class="cat-faq-chevron">
                        <i class="bi bi-chevron-down"></i>
                      </span>
                    </button>
                    <div v-if="openFaqItems.has(item.id)" class="cat-faq-a" v-html="item.answer"></div>
                  </div>
                </div>
              </template>

              <!-- Children van de groep -->
              <template v-if="group.children && group.children.length">
                <template v-for="child in group.children" :key="child.id">
                  <div v-if="child.name" class="cat-faq-group-label">{{ child.name }}</div>
                  <div class="cat-faq-list">
                    <div
                      v-for="item in child.items"
                      :key="item.id"
                      class="cat-faq-item"
                      :class="{ open: openFaqItems.has(item.id) }"
                    >
                      <button class="cat-faq-q" @click="toggleFaq(item.id)">
                        <span>{{ item.question }}</span>
                        <span class="cat-faq-chevron">
                          <i class="bi bi-chevron-down"></i>
                        </span>
                      </button>
                      <div v-if="openFaqItems.has(item.id)" class="cat-faq-a" v-html="item.answer"></div>
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </div>

          <p class="cat-faq-more">
            Meer vragen? Bekijk onze
            <router-link to="/veelgestelde-vragen" class="cat-faq-more-link">volledige FAQ-pagina</router-link>
            of
            <router-link to="/klantenservice" class="cat-faq-more-link">neem contact op</router-link>.
          </p>
        </div>
      </section>

      <!-- Contact banner -->
      <ContactBanner />

      <!-- SEO-tekst sectie - alleen voor leaf-categorieën (landing heeft eigen SEO-blok) -->
      <section v-if="category.description && !isLandingPage" class="seo-section">
        <div class="container">
          <h2 class="seo-title">Over {{ category.name }}</h2>
          <div class="seo-body" :class="{ 'is-truncated': !seoExpanded && isLongDescription }">
            <div class="seo-content" :class="{ 'is-plaintext': descriptionIsPlain }" v-html="category.description"></div>
            <div v-if="!seoExpanded && isLongDescription" class="seo-fade"></div>
          </div>
          <button
            v-if="isLongDescription"
            class="btn-seo-toggle"
            @click="seoExpanded = !seoExpanded"
          >
            <span>{{ seoExpanded ? 'Lees minder' : 'Lees meer' }}</span>
            <i :class="seoExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </button>
        </div>
      </section>
    </div>
    
    <!-- Mobile Filters Overlay -->
    <div 
      v-if="showFiltersPanel" 
      class="filters-overlay"
      @click="showFiltersPanel = false"
    ></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProductCard from '@components/ProductCard.vue'
import ProductListCard from '@components/ProductListCard.vue'
import ContactBanner from '@components/ContactBanner.vue'
import { cachedFetch } from '@/composables/useApiCache.js'

const route  = useRoute()
const router = useRouter()

// HTML-entities decoderen (WooCommerce stuurt bijv. "Montage &amp; Accessoires")
const decodeHtml = (str) => {
  if (!str) return str
  return String(str)
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#039;/g, "'").replace(/&nbsp;/g, ' ')
}
const decodeCat = (cat) => cat ? { ...cat, name: decodeHtml(cat.name), description: decodeHtml(cat.description) } : cat

// ─── State ────────────────────────────────────────────────────────────────────
const loading         = ref(true)
const productsLoading = ref(false)
const error           = ref(false)
const category        = ref(null)
const parentCategory  = ref(null)
const subcategories   = ref([])
const products        = ref([])
const relatedCategories = ref([])
const categoryFaqs    = ref([])
const openFaqItems    = ref(new Set())

const sortBy   = ref('menu_order')
const viewMode = ref(
  (typeof localStorage !== 'undefined' && localStorage.getItem('p2c_view_mode')) || 'grid'
)
const showFiltersPanel = ref(false)
const currentPage     = ref(1)
const productsPerPage = 24
const seoExpanded     = ref(false)

const priceFilter    = ref({ min: null, max: null })
const selectedBrands = ref([])
const filters        = ref({ inStock: false, onSale: false })

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Centrale fetch via de Express proxy - met client-side TTL cache.
 * Categorieën: 30 min TTL. Producten: 10 min TTL.
 * Herhaalde navigatie naar dezelfde categorie is daardoor razendsnel.
 */
const wcFetch = (endpoint) => {
  const url = `/_wc/v3/${endpoint}`
  const ttl = endpoint.includes('categories') ? 30 * 60 * 1000 : 10 * 60 * 1000
  return cachedFetch(url, ttl)
}

// ─── Computed ─────────────────────────────────────────────────────────────────
const categorySlug = computed(() => route.params.category)

const hasSubcategories = computed(() => subcategories.value.length > 0)

// Landing-page modus - drie mogelijke bronnen (volgorde van prioriteit):
//   1. WordPress meta _is_landing_page = '1' → altijd landingspagina
//   2. WordPress meta _is_landing_page = '0' → altijd productenweergave
//   3. Geen meta (leeg) → automatisch: landingspagina als er subcategorieën zijn
const isLandingPage = computed(() => {
  const meta = category.value?._is_landing_page
  if (meta === '1') return true
  if (meta === '0') return false
  return hasSubcategories.value  // automatisch fallback
})

const availableBrands = computed(() => {
  const brands = new Set()
  products.value.forEach(product => {
    product.attributes?.forEach(attr => {
      if (attr.name === 'Merk' || attr.slug === 'pa_merk') {
        attr.options?.forEach(o => brands.add(o))
      }
    })
  })
  return Array.from(brands).sort()
})

const currentPriceRange = computed(() => {
  if (!products.value.length) return { min: 0, max: 1000 }
  const prices = products.value.map(p => parseFloat(p.price) || 0)
  return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) }
})

const filteredProducts = computed(() => {
  let list = [...products.value]

  if (priceFilter.value.min !== null)
    list = list.filter(p => parseFloat(p.price) >= priceFilter.value.min)
  if (priceFilter.value.max !== null)
    list = list.filter(p => parseFloat(p.price) <= priceFilter.value.max)

  if (selectedBrands.value.length) {
    list = list.filter(product => {
      const productBrands = product.attributes
        ?.find(a => a.name === 'Merk' || a.slug === 'pa_merk')
        ?.options || []
      return productBrands.some(b => selectedBrands.value.includes(b))
    })
  }

  if (filters.value.inStock) list = list.filter(p => p.stock_status === 'instock' || p.backorders_allowed)
  if (filters.value.onSale)  list = list.filter(p => p.on_sale)

  switch (sortBy.value) {
    case 'popularity':  list.sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0)); break
    case 'rating':      list.sort((a, b) => parseFloat(b.average_rating || 0) - parseFloat(a.average_rating || 0)); break
    case 'date':        list.sort((a, b) => new Date(b.date_created) - new Date(a.date_created)); break
    case 'price':       list.sort((a, b) => parseFloat(a.price) - parseFloat(b.price)); break
    case 'price-desc':  list.sort((a, b) => parseFloat(b.price) - parseFloat(a.price)); break
    default:            list.sort((a, b) => (a.menu_order || 0) - (b.menu_order || 0)); break
  }

  return list
})

const totalPages = computed(() => Math.ceil(filteredProducts.value.length / productsPerPage))

const paginatedProducts = computed(() => {
  const start = (currentPage.value - 1) * productsPerPage
  return filteredProducts.value.slice(start, start + productsPerPage)
})

const visiblePages = computed(() => {
  const pages = [], total = totalPages.value, cur = currentPage.value
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i)
  } else if (cur <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i)
    pages.push('...', total)
  } else if (cur >= total - 3) {
    pages.push(1, '...')
    for (let i = total - 4; i <= total; i++) pages.push(i)
  } else {
    pages.push(1, '...', cur - 1, cur, cur + 1, '...', total)
  }
  return pages
})

const isLongDescription = computed(() => {
  if (!category.value?.description) return false
  const plain = category.value.description.replace(/<[^>]*>/g, '')
  return plain.length > 400
})

// WordPress stript soms blok-tags (h2/p/ul/img) uit de categoriebeschrijving en
// laat alleen inline-tags + regeleindes over. In dat geval tonen we de regels
// met white-space:pre-line zodat het geen tekstmuur wordt. Zodra WP wél echte
// HTML teruggeeft, is dit false en rendert alles normaal.
const descriptionIsPlain = computed(() => {
  const d = category.value?.description || ''
  return d.length > 0 && !/<(p|h[1-6]|ul|ol|div|br|table)[\s>/]/i.test(d)
})

const activeFiltersCount = computed(() => {
  let n = 0
  if (priceFilter.value.min !== null || priceFilter.value.max !== null) n++
  n += selectedBrands.value.length
  if (filters.value.inStock) n++
  if (filters.value.onSale)  n++
  return n
})

// ─── Methods ──────────────────────────────────────────────────────────────────
const getCategoryIcon = (slug) => ({
  'zonnepanelen':    'bi bi-sun',
  'complete-sets':   'bi bi-box-seam',
  'laadpalen':       'bi bi-lightning-charge',
  'laadkabels':      'bi bi-plug',
  'micro-omvormers': 'bi bi-cpu',
  'thuisbatterijen':      'bi bi-battery',
  'montage':         'bi bi-hammer',
  'kabels':          'bi bi-plug',
  'monitoring':      'bi bi-bar-chart-line',
  'accessoires':     'bi bi-gear',
  'smart-home':      'bi bi-house',
})[slug] || 'bi bi-folder'

const getCategoryType = (slug) => ({
  'zonnepanelen': 'Zonne-energie',
  'laadpalen':    'Laden',
  'accessoires':  'Accessoires'
})[slug] || 'Producten'

const getExcerpt = (html, max = 150) => {
  if (!html) return ''
  const text = html.replace(/<[^>]*>/g, '')
  return text.length > max ? text.substring(0, max) + '...' : text
}

const formatPrice = (price) => {
  if (!price && price !== 0) return '€0,00'
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(price)
}

const getBrandCount = (brand) => products.value.filter(product => {
  const productBrands = product.attributes
    ?.find(a => a.name === 'Merk' || a.slug === 'pa_merk')
    ?.options || []
  return productBrands.includes(brand)
}).length

const clearPriceFilter = () => { priceFilter.value = { min: null, max: null } }
const removeBrand      = (brand) => { selectedBrands.value = selectedBrands.value.filter(b => b !== brand) }

const resetFilters = () => {
  priceFilter.value  = { min: null, max: null }
  selectedBrands.value = []
  filters.value      = { inStock: false, onSale: false }
  currentPage.value  = 1
}

const onSortChange = () => {
  currentPage.value = 1
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value || page === '...') return
  currentPage.value = page
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// ─── Data fetching ────────────────────────────────────────────────────────────
const fetchCategoryData = async () => {
  try {
    loading.value = true
    error.value   = false

    // 1. Categorie ophalen via slug (enige blokkerende aanvraag)
    const cats = await wcFetch(`products/categories?slug=${categorySlug.value}`)
    if (!cats?.length) throw new Error('Categorie niet gevonden')
    const raw = cats[0]
    // Lees _is_landing_page uit meta_data ('1' = altijd landing, '0' = altijd producten, '' = automatisch)
    const landingMeta = (raw.meta_data || []).find(m => m.key === '_is_landing_page')?.value ?? ''
    category.value = { ...decodeCat(raw), _is_landing_page: landingMeta }

    // 2. Pagina renderen zodra de categorie bekend is
    loading.value = false
    document.title = `${category.value.name} | Plug2charge`

    // 3. Producten starten als eerste - worden zichtbaar zodra ze klaar zijn
    fetchProducts()

    // 4. Rest op de achtergrond (niet awaited, vullen zich in wanneer gereed)
    if (category.value.parent !== 0) {
      wcFetch(`products/categories/${category.value.parent}`)
        .then(p => { parentCategory.value = decodeCat(p) })
        .catch(() => {})
    }
    fetchSubcategories()
    fetchRelatedCategories()
    fetchCategoryFaqs()

  } catch (err) {
    console.error('❌ Categorie laden mislukt:', err.message)
    error.value = true
    loading.value = false
  }
}

const fetchSubcategories = async () => {
  try {
    const rawSubs = await wcFetch(
      `products/categories?parent=${category.value.id}&per_page=100`
    )
    subcategories.value = (rawSubs || []).map(decodeCat)
  } catch (err) {
    console.error('❌ Subcategorieën laden mislukt:', err.message)
    subcategories.value = []
  }
}

const fetchProducts = async () => {
  try {
    productsLoading.value = true
    products.value = await wcFetch(
      `products?category=${category.value.id}&per_page=100&status=publish`
    )
  } catch (err) {
    console.error('❌ Producten laden mislukt:', err.message)
    products.value = []
  } finally {
    productsLoading.value = false
  }
}

const fetchCategoryFaqs = async () => {
  try {
    const res = await fetch(`/_faq?for=${categorySlug.value}`)
    if (!res.ok) return
    const data = await res.json()
    categoryFaqs.value = data || []
    // Open het eerste item van elk FAQ-blok automatisch
    const firstIds = new Set()
    data.forEach(g => {
      if (g.items?.[0]) firstIds.add(g.items[0].id)
      ;(g.children || []).forEach(c => { if (c.items?.[0]) firstIds.add(c.items[0].id) })
    })
    openFaqItems.value = firstIds
  } catch {}
}

const toggleFaq = (id) => {
  const next = new Set(openFaqItems.value)
  next.has(id) ? next.delete(id) : next.add(id)
  openFaqItems.value = next
}

const fetchRelatedCategories = async () => {
  try {
    const parentId = category.value.parent || 0
    // hide_empty + orderby count: geen lege categorieën, en de best-gevulde eerst.
    const cats = await wcFetch(
      `products/categories?parent=${parentId}&per_page=20&hide_empty=true&orderby=count&order=desc`
    )
    relatedCategories.value = cats
      .filter(c => c.id !== category.value.id && c.count > 0 && c.slug !== 'uncategorized')
      .slice(0, 4)
  } catch (err) {
    console.error('❌ Gerelateerde categorieën laden mislukt:', err.message)
    relatedCategories.value = []
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(fetchCategoryData)

watch(() => route.params.category, (slug) => {
  if (slug) {
    resetFilters()
    seoExpanded.value = false
    categoryFaqs.value = []
    openFaqItems.value = new Set()
    fetchCategoryData()
  }
})

watch(filteredProducts, () => { currentPage.value = 1 })

// Bewaar viewMode in localStorage zodat de voorkeur blijft na navigatie
watch(viewMode, (val) => {
  try { localStorage.setItem('p2c_view_mode', val) } catch {}
})
</script>

<style scoped>
/* ============================================
   CATEGORY PAGE - PLUG2CHARGE STYLE
   ============================================ */

/* Container - zelfde als de rest van de site */
.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.25rem;
}

.category-page {
  background: #f8f9fa;
  min-height: 100vh;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 3rem 2rem;
}

.spinner {
  width: 4rem; height: 4rem;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #FFD500;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin { to { transform: rotate(360deg); } }

.error-icon {
  width: 100px; height: 100px;
  background: #fee; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 2rem;
}
.error-icon i { font-size: 3rem; color: #dc3545; }
.error-container h3 { font-size: 1.75rem; font-weight: 700; margin-bottom: .75rem; }
.error-container p  { color: #666; margin-bottom: 2rem; }

.btn-back {
  background: #FFD500;
  color: #000; padding: .875rem 2rem; border-radius: 8px;
  text-decoration: none; font-weight: 700;
  display: inline-flex; align-items: center; gap: .75rem;
  transition: background .15s ease;
}
.btn-back:hover { background: #ffb800; }

/* Breadcrumb */
.breadcrumb-section { background: #fff; padding: 1rem 0; border-bottom: 1px solid #f3f4f6; }
.breadcrumb-nav { display: flex; align-items: center; gap: .75rem; font-size: .875rem; flex-wrap: wrap; }
.breadcrumb-link { color: #666; text-decoration: none; transition: color .2s; display: flex; align-items: center; gap: .5rem; }
.breadcrumb-link:hover { color: #FFD500; }
.breadcrumb-nav > i { font-size: .625rem; color: #ccc; }
.breadcrumb-current { color: #000; font-weight: 600; }

/* Hero */
.category-hero {
  position: relative;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
  color: #111; padding: 2.5rem 0; overflow: hidden;
}
.hero-overlay { display: none; }
.hero-background { display: none; }
.hero-content { position: relative; z-index: 2; max-width: 800px; }

.category-badge {
  display: inline-flex; align-items: center; gap: .5rem;
  background: none; border: none; color: #9a842b;
  padding: 0;
  font-weight: 700; font-size: .75rem; text-transform: uppercase; letter-spacing: 1.4px;
  margin-bottom: 1rem;
}
.category-badge i { font-size: .8rem; color: #c9a900; }

.category-title { font-size: clamp(1.9rem, 4.6vw, 3rem); font-weight: 900; color: #111; margin-bottom: .75rem; line-height: 1.08; letter-spacing: -1px; text-wrap: balance; }
.category-subtitle { font-size: 1.0625rem; color: #555; line-height: 1.7; margin-bottom: 1.75rem; max-width: 60ch; text-wrap: pretty; }

/* Stats - lichte inline-rij met hairline-scheiding (geen icoonblokjes) */
.category-stats { display: flex; gap: 0; flex-wrap: wrap; }
.stat-item { display: flex; align-items: center; gap: .625rem; padding: 0 1.5rem; }
.stat-item:first-child { padding-left: 0; }
.stat-item + .stat-item { border-left: 1px solid #ececec; }
.stat-icon { display: none; }
.stat-number { font-size: 1.4rem; font-weight: 800; color: #111; line-height: 1; margin-bottom: .15rem; letter-spacing: -.4px; }
.stat-label  { font-size: .75rem; color: #888; }

/* Desktop: uitgeklede hero - de 'Bekijk alle …'-knop en de stats-balk zijn hier
   overbodig (producten staan er direct onder). Op mobiel blijven ze staan. */
@media (min-width: 1024px) {
  .category-hero:not(.hero-landing) .hero-cta-row,
  .category-hero:not(.hero-landing) .category-stats { display: none; }
}

/* ── Hero: landing-variant (extra accenten bovenop de basis-hero) ── */
.category-hero.hero-landing {
  padding: 3.5rem 0 3rem;
  background:
    radial-gradient(120% 90% at 100% -10%, rgba(255,213,0,.09), transparent 55%),
    #fff;
}
.hero-landing .hero-content { max-width: 780px; }
.hero-landing .category-title { font-size: clamp(2.1rem, 5.2vw, 3.4rem); letter-spacing: -1.2px; line-height: 1.04; }
/* Stats zijn op de landing-variant overbodig: verzending/garantie staan al in
   de trust-strip en het aantal producten staat op elke subcategorie-kaart. */
.hero-landing .category-stats { display: none; }
.hero-landing .category-subtitle { margin-bottom: 0; }

/* Subcategories chip bar */
.subcategories-section {
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
  padding: .875rem 0;
}

.subcat-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
}

.subcat-bar-label {
  display: flex;
  align-items: center;
  gap: .4rem;
  font-size: .75rem;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: .5px;
  white-space: nowrap;
  flex-shrink: 0;
}
.subcat-bar-label i { color: #FFD500; font-size: .8rem; }

.subcat-chips-scroll {
  display: flex;
  gap: .5rem;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 2px; /* prevent clipping box-shadow */
}
.subcat-chips-scroll::-webkit-scrollbar { display: none; }

.subcat-chip {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: .4rem .875rem .4rem .5rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 50px;
  background: #f9fafb;
  text-decoration: none;
  color: #333;
  font-size: .8rem;
  font-weight: 600;
  white-space: nowrap;
  transition: border-color .15s, background .15s, color .15s;
  flex-shrink: 0;
}
.subcat-chip:hover {
  border-color: #FFD500;
  background: #fffbeb;
  color: #111;
}

.subcat-chip-img {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.subcat-chip-icon {
  font-size: .85rem;
  color: #FFD500;
  width: 22px;
  text-align: center;
}

.subcat-chip-count {
  font-size: .7rem;
  font-weight: 700;
  color: #bbb;
  background: #f0f0f0;
  padding: 1px 6px;
  border-radius: 50px;
  margin-left: .1rem;
}
.subcat-chip:hover .subcat-chip-count {
  background: #FFD500;
  color: #111;
}

/* ── Hero CTA (denotenshop-stijl) ── */
.hero-cta-row { margin-top: 1.25rem; }
.hero-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  background: #FFD500;
  color: #111;
  font-weight: 700;
  font-size: .8125rem;
  padding: .5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  transition: background .15s, transform .15s;
  box-shadow: 0 2px 8px rgba(255,213,0,.28);
}
.hero-cta-btn:hover { background: #ffb800; transform: translateY(-1px); }

/* ── Subcategorie kaartjes (denotenshop-stijl) ── */
.subcat-cards-section { padding: 1.5rem 0 0; background: #f6f7f9; }
.subcat-cards-title {
  display: flex;
  align-items: center;
  gap: .5rem;
  font-size: .8rem;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: .5px;
  margin: 0 0 1rem;
}
.subcat-cards-title i { color: #FFD500; }
.subcat-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: .875rem;
  margin-bottom: 1.5rem;
}
.subcat-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1.5px solid #e9eaec;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  color: #111;
  transition: border-color .15s, box-shadow .15s, transform .15s;
  position: relative;
}
.subcat-card:hover {
  border-color: #FFD500;
  box-shadow: 0 4px 16px rgba(255,213,0,.15);
  transform: translateY(-2px);
}
.subcat-card-media {
  width: 100%;
  aspect-ratio: 4/3;
  background: #f8f9fa;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.subcat-card-img { width: 100%; height: 100%; object-fit: cover; }
.subcat-card-icon {
  font-size: 2rem;
  color: #FFD500;
}
.subcat-card-body {
  padding: .625rem .75rem .75rem;
  display: flex;
  flex-direction: column;
  gap: .2rem;
  flex: 1;
}
.subcat-card-name {
  font-size: .85rem;
  font-weight: 700;
  color: #111;
  line-height: 1.3;
}
.subcat-card-count {
  font-size: .72rem;
  color: #999;
  font-weight: 500;
}
.subcat-card-arrow {
  position: absolute;
  bottom: .75rem;
  right: .75rem;
  font-size: .75rem;
  color: #ccc;
  transition: color .15s, right .15s;
}
.subcat-card:hover .subcat-card-arrow { color: #FFD500; right: .5rem; }

/* Products section */
.products-section { padding: 1.5rem 0; }

.filters-topbar {
  background: #fff; border: 1.5px solid #e9eaec; border-radius: 8px;
  padding: 0.75rem 1rem; margin-bottom: 1.5rem;
  display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap;
}
.topbar-left  { display: flex; align-items: center; gap: 2rem; flex-wrap: wrap; }
.topbar-right { display: flex; align-items: center; gap: 1.5rem; }

.btn-filters {
  background: #f8f9fa; border: 1.5px solid #e9eaec; color: #111;
  padding: .5rem 1rem; border-radius: 8px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: .625rem; transition: all .15s ease; font-size: .875rem;
}
.btn-filters:hover, .btn-filters.active { background: #FFD500; border-color: #FFD500; }
.filter-badge { background: #000; color: #FFD500; padding: 2px 8px; border-radius: 12px; font-size: .75rem; font-weight: 700; min-width: 20px; text-align: center; }
.results-info { display: flex; align-items: baseline; gap: .5rem; font-size: .9375rem; }
.results-info strong { font-size: 1.25rem; font-weight: 900; color: #000; }
.results-info span  { color: #666; }

.sort-wrapper { display: flex; align-items: center; gap: .75rem; }
.sort-wrapper label { font-size: .875rem; font-weight: 600; color: #666; display: flex; align-items: center; gap: .5rem; }
.sort-select { background: #f8f9fa; border: 1.5px solid #e9eaec; padding: .5rem .875rem; border-radius: 8px; font-weight: 600; cursor: pointer; transition: border-color .2s ease; font-size: .875rem; }
.sort-select:hover, .sort-select:focus { border-color: #FFD500; outline: none; }

.view-toggle { display: flex; gap: .5rem; background: #f8f9fa; padding: .25rem; border-radius: 8px; }
.view-btn { background: transparent; border: none; width: 40px; height: 40px; border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #666; transition: all .2s; }
.view-btn:hover { color: #000; }
.view-btn.active { background: #FFD500; color: #000; }

.active-filters-bar {
  background: #fff; border: 1.5px solid #e9eaec; border-radius: 8px;
  padding: .625rem 1rem; margin-bottom: 1.25rem;
  display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap;
}
.filters-pills { display: flex; align-items: center; gap: .75rem; flex-wrap: wrap; flex: 1; }
.pill-label { font-weight: 700; font-size: .875rem; color: #666; }
.filter-pill {
  background: #FFD500; border: 1.5px solid #FFD500; color: #111;
  padding: .3rem .75rem; border-radius: 50px; font-weight: 700; font-size: .8rem;
  display: flex; align-items: center; gap: .4rem; cursor: pointer; transition: background .15s, opacity .15s;
}
.filter-pill:hover { background: #ffb800; border-color: #ffb800; }
.filter-pill i:last-child { font-size: .75rem; }
.btn-clear-all {
  background: transparent; border: 2px solid #e9ecef; color: #666;
  padding: .5rem 1rem; border-radius: 8px; font-weight: 600; font-size: .875rem;
  cursor: pointer; display: flex; align-items: center; gap: .5rem; transition: all .2s; white-space: nowrap;
}
.btn-clear-all:hover { border-color: #dc3545; color: #dc3545; }

.content-grid { display: grid; grid-template-columns: 260px 1fr; gap: 2rem; align-items: start; }

/* Sidebar */
.filters-sidebar { background: #fff; border: 1.5px solid #e9eaec; border-radius: 12px; overflow: hidden; position: sticky; top: 165px; }
.sidebar-header { background: #fff; padding: 1rem 1.25rem; border-bottom: 1px solid #f0f0f0; display: flex; justify-content: space-between; align-items: center; }
.sidebar-header h3 { font-size: 0.9rem; font-weight: 700; color: #111; display: flex; align-items: center; gap: .5rem; margin: 0; text-transform: uppercase; letter-spacing: .5px; }
.sidebar-header h3 i { color: #FFD500; font-size: .9rem; }
.btn-close-filters { display: none; background: none; border: none; font-size: 1.5rem; color: #666; cursor: pointer; padding: 0; width: 32px; height: 32px; align-items: center; justify-content: center; }
.btn-close-filters:hover { color: #000; }
.sidebar-body { padding: 1.5rem; max-height: calc(100vh - 300px); overflow-y: auto; }

.filter-group { margin-bottom: 1.5rem; padding-bottom: 1.5rem; border-bottom: 1px solid #f3f4f6; }
.filter-group:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }
.filter-title { font-size: .75rem; font-weight: 700; color: #888; margin-bottom: .75rem; display: flex; align-items: center; gap: .5rem; text-transform: uppercase; letter-spacing: .5px; }
.filter-title i { color: #FFD500; font-size: .75rem; }

.price-inputs { display: flex; align-items: center; gap: .5rem; margin-bottom: .75rem; }
.price-input-wrapper { flex: 1; position: relative; min-width: 0; }
.price-input-wrapper .currency { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #666; font-weight: 600; pointer-events: none; }
.price-input { width: 100%; min-width: 0; padding: .625rem .5rem .625rem 1.5rem; border: 1.5px solid #e9eaec; border-radius: 8px; font-weight: 600; transition: border-color .2s;
  -webkit-appearance: none; -moz-appearance: textfield; appearance: textfield; }
/* Nummer-spinner pijltjes verbergen - die maakten de velden te smal (placeholder afgekapt) */
.price-input::-webkit-outer-spin-button,
.price-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.price-separator { flex-shrink: 0; }
.price-input:focus { outline: none; border-color: #FFD500; }
.price-separator { color: #999; font-weight: 700; }
.price-range-info { font-size: .8125rem; color: #666; text-align: center; }

.filter-options { display: flex; flex-direction: column; gap: 2px; }
.filter-option { display: flex; align-items: center; gap: .625rem; padding: .5rem .25rem; cursor: pointer; transition: none; }
.filter-option:hover .custom-check { border-color: #FFD500; }
.option-label { flex: 1; color: #555; font-size: .875rem; transition: color .1s; }
.option-label.font-bold { color: #111; font-weight: 600; }
.option-count { color: #bbb; font-size: .72rem; font-weight: 500; }
.stock-dot { color: #22c55e; font-size: .7rem; }
.sale-dot { background: #FFD500; color: #000; padding: 1px 5px; border-radius: 3px; font-weight: 700; font-size: .65rem; }

/* Custom checkbox - zelfde als Products.vue */
.custom-check {
  width: 18px; height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: border-color .15s, background .15s;
  font-size: .7rem;
  color: #111;
}
.custom-check.checked {
  background: #FFD500;
  border-color: #FFD500;
}

.sidebar-footer { padding: 1rem 1.25rem; border-top: 1px solid #f0f0f0; display: flex; flex-direction: column; gap: .625rem; }
.btn-reset-filters { background: transparent; border: 1px solid #e5e7eb; color: #666; padding: .75rem 1rem; border-radius: 7px; font-weight: 600; font-size: .875rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: .5rem; transition: border-color .15s, color .15s; }
.btn-reset-filters:hover { border-color: #dc3545; color: #dc3545; }
.btn-apply-filters { background: #FFD500; border: none; color: #000; padding: .75rem 1rem; border-radius: 7px; font-weight: 700; font-size: .875rem; cursor: pointer; transition: background .15s; }
.btn-apply-filters:hover { background: #ffb800; }

/* Products */
.products-area { min-height: 400px; }
.products-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; }
.products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 2rem; margin-bottom: 3rem; }
.products-grid.list-view { grid-template-columns: 1fr; }

.no-products { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 5rem 2rem; text-align: center; background: #fff; border: 1.5px dashed #e9eaec; border-radius: 12px; }
.no-products-icon { width: 80px; height: 80px; background: #f8f8f8; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem; }
.no-products-icon i { font-size: 2.5rem; color: #ddd; }
.no-products h3 { font-size: 1.5rem; font-weight: 800; color: #111; margin-bottom: .625rem; }
.no-products p  { color: #666; margin-bottom: 2rem; max-width: 420px; line-height: 1.65; }
.no-products-actions { display: flex; align-items: center; gap: .875rem; flex-wrap: wrap; justify-content: center; }
.btn-reset-large { background: #FFD500; color: #111; padding: .75rem 1.75rem; border-radius: 8px; border: none; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: .625rem; transition: background .15s, transform .15s; font-size: .9rem; }
.btn-reset-large:hover { background: #ffb800; transform: translateY(-1px); }
.btn-all-products { background: #fff; color: #111; padding: .75rem 1.75rem; border-radius: 8px; border: 1.5px solid #e9eaec; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: .625rem; text-decoration: none; transition: border-color .15s, background .15s; font-size: .9rem; }
.btn-all-products:hover { border-color: #FFD500; background: #fffbeb; }

/* Pagination */
.pagination { display: flex; justify-content: center; align-items: center; gap: .5rem; padding: 2rem 0; flex-wrap: wrap; }
.page-btn { background: #fff; border: 1.5px solid #e9eaec; color: #111; padding: .5rem 1.125rem; border-radius: 50px; font-weight: 700; font-size: .875rem; cursor: pointer; display: flex; align-items: center; gap: .375rem; transition: all .15s; }
.page-btn:hover:not(:disabled) { border-color: #FFD500; background: #fffbeb; }
.page-btn:disabled { opacity: .4; cursor: not-allowed; }
.page-numbers { display: flex; gap: .375rem; flex-wrap: wrap; }
.page-number { background: #fff; border: 1.5px solid #e9eaec; color: #111; width: 40px; height: 40px; border-radius: 50px; font-weight: 700; font-size: .875rem; cursor: pointer; transition: all .15s; display: flex; align-items: center; justify-content: center; }
.page-number:hover:not(.ellipsis):not(.active) { border-color: #FFD500; background: #fffbeb; }
.page-number.active { background: #FFD500; border-color: #FFD500; color: #111; }
.page-number.ellipsis { border: none; cursor: default; color: #aaa; }

/* Trust banner - minimal: geen kaartjes, hairline-scheiding */
.trust-banner { background: #fafafa; padding: 1.25rem 0; margin: 0; border-top: 1px solid #f1f2f4; border-bottom: 1px solid #f1f2f4; }
.trust-content { display: flex; align-items: center; flex-wrap: wrap; gap: 1rem 0; }
.trust-item { display: flex; align-items: center; gap: .7rem; flex: 1; min-width: 190px; padding: 0 1.5rem; }
.trust-item:first-child { padding-left: 0; }
.trust-item + .trust-item { border-left: 1px solid #e8e9ec; }
.trust-icon { width: auto; height: auto; background: none; border-radius: 0; flex-shrink: 0; display: flex; align-items: center; }
.trust-icon i { color: #c9a900; font-size: 1.35rem; }
.trust-item strong { display: block; font-size: .9rem; font-weight: 700; color: #111; margin-bottom: .1rem; letter-spacing: -.1px; }
.trust-item span   { display: block; font-size: .78rem; color: #999; }

/* Related */
.related-section { padding: 1.5rem 0; }
.related-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 1rem; }
.related-card { background: #fff; border: 2px solid #e9ecef; border-radius: 12px; padding: 1.5rem; text-decoration: none; display: flex; align-items: center; gap: 1rem; transition: all .3s; }
.related-card:hover { border-color: #FFD500; transform: translateX(8px); box-shadow: 0 8px 24px rgba(0,0,0,.08); }
.related-icon { width: 60px; height: 60px; background: linear-gradient(135deg, #f8f9fa, #e9ecef); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.related-icon i { color: #FFD500; font-size: 1.5rem; }
.related-info { flex: 1; }
.related-info h4 { font-size: 1.0625rem; font-weight: 700; color: #000; margin-bottom: .25rem; }
.related-info span { font-size: .875rem; color: #666; }
.related-arrow { color: #FFD500; font-size: 1rem; transition: transform .2s; }
.related-card:hover .related-arrow { transform: translateX(4px); }

/* SEO-tekst sectie */
.seo-section {
  padding: 2rem 0;
  background: #fff;
  border-top: 1px solid #f3f4f6;
}
.seo-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111;
  margin-bottom: 1.25rem;
  letter-spacing: -.25px;
}
.seo-body {
  position: relative;
}
.seo-body.is-truncated {
  max-height: 120px;
  overflow: hidden;
}
.seo-fade {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 60px;
  background: linear-gradient(to bottom, transparent, #fff);
  pointer-events: none;
}
.seo-content {
  font-size: .9375rem;
  color: #555;
  line-height: 1.75;
  max-width: 860px;
}
.seo-content h1, .seo-content h2, .seo-content h3 {
  color: #111; font-weight: 700; margin: 1.25rem 0 .5rem;
}
.seo-content p { margin-bottom: .875rem; }
.seo-content ul, .seo-content ol { padding-left: 1.5rem; margin-bottom: .875rem; }
.seo-content li { margin-bottom: .375rem; }
.seo-content a { color: #7a5f00; text-underline-offset: 3px; }
.seo-content a:hover { color: #FFD500; }
/* Vangnet: als WordPress de blok-tags heeft gestript, tonen we de regeleindes
   zodat de tekst niet als één blok samenklit. */
.seo-content.is-plaintext,
.landing-seo-body.is-plaintext { white-space: pre-line; }
.btn-seo-toggle {
  margin-top: 1rem;
  display: inline-flex; align-items: center; gap: .5rem;
  background: none; border: 1px solid #e9ecef;
  color: #555; font-size: .875rem; font-weight: 600;
  padding: .5rem 1.125rem; border-radius: 8px; cursor: pointer;
  transition: border-color .15s, color .15s;
}
.btn-seo-toggle:hover { border-color: #FFD500; color: #111; }

/* Overlay */
.filters-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 99; backdrop-filter: blur(4px); }

/* Responsive */
@media (max-width: 1199px) {
  .content-grid { grid-template-columns: 240px 1fr; gap: 1.5rem; }
}

@media (max-width: 991px) {
  .content-grid { grid-template-columns: 1fr; }
  .filters-sidebar { position: fixed; top: 0; left: 0; width: 100%; max-width: 400px; height: 100%; z-index: 100; transform: translateX(-100%); transition: transform .3s ease; border-radius: 0; }
  .filters-sidebar.is-open { transform: translateX(0); }
  .filters-overlay { display: block; }
  .btn-close-filters { display: flex; }
  .sidebar-body { max-height: calc(100vh - 200px); }
}

@media (max-width: 767px) {
  .category-hero { padding: 1.25rem 0; }
  .category-title { font-size: 1.5rem; }
  .category-subtitle { display: none; } /* verborgen op mobiel - minder scrollen voor producten */
  .category-stats { display: none; }    /* stats verbergen op mobiel - nemen teveel hoogte */
  .subcat-bar-label { display: none; }
  .filters-topbar { padding: 0.625rem 0.75rem; gap: 0.5rem; border-radius: 8px; }
  .topbar-left { flex: 1; gap: 0.5rem; }
  .topbar-right { gap: 0.5rem; }
  .sort-select { max-width: 120px; font-size: 0.8rem; padding: 0.4rem 0.5rem; }
  .sort-wrapper label { display: none; }
  .products-grid { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; } /* 2-kolom grid op mobiel */
  .related-grid { grid-template-columns: 1fr; }
  .filters-sidebar { max-width: 100%; }
  .landing-card-media { aspect-ratio: 3/2; } /* minder hoog op mobiel */
  .landing-intro { padding: 1rem 0; }
  .landing-grid-section { padding: 1.5rem 0 2rem; }
  .category-badge { font-size: 0.68rem; margin-bottom: 0.625rem; }
  .trust-content { gap: 0; }
  .trust-item { flex: 1 1 46%; padding: .5rem .5rem; }
  .trust-item:nth-child(odd) { padding-left: 0; }
  .trust-item + .trust-item { border-left: none; }
}

@media (max-width: 480px) {
  .category-title { font-size: 1.375rem; }
  .active-filters-bar { flex-direction: column; align-items: stretch; }
  .btn-clear-all { width: 100%; justify-content: center; }
  .pagination { flex-wrap: wrap; gap: .375rem; }
  .page-btn span { display: none; }
  .page-btn { padding: .5rem .75rem; }
  .page-number { width: 36px; height: 36px; font-size: .8rem; }
  .products-grid { grid-template-columns: repeat(2, 1fr); gap: 0.5rem; }
  .landing-grid { gap: 0.875rem; }
  .no-products-actions { flex-direction: column; align-items: stretch; }
  .btn-reset-large, .btn-all-products { justify-content: center; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; }
}

button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible {
  outline: 3px solid #FFD500;
  outline-offset: 2px;
}

/* ═══════════════════════════════════════════════════════════════════
   SKELETON LOADER
   ═══════════════════════════════════════════════════════════════════ */

@keyframes shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}
.skeleton-img,
.skeleton-line,
.skeleton-price,
.skeleton-list-img,
.skeleton-list-btn,
.skeleton-card,
.skeleton-list-card {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
}

/* Grid skeleton kaart */
.skeleton-card {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e9eaec;
  background: #fff; /* eigen achtergrond */
  animation: none;  /* kaart zelf shimmer niet */
}
.skeleton-img {
  width: 100%;
  aspect-ratio: 1 / 1; /* overeenkomstig ProductCard image ratio */
  border-radius: 0;
}
.skeleton-body {
  padding: 12px 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.skeleton-line {
  height: 12px;
  border-radius: 6px;
}
.skeleton-line.mt-2 { margin-top: 4px; }
.skeleton-price {
  height: 22px;
  border-radius: 6px;
  width: 45%;
  margin-top: 8px;
}

/* List skeleton kaart */
.skeleton-list-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e9eaec;
  background: #fff;
  animation: none;
}
.skeleton-list-img {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  flex-shrink: 0;
}
.skeleton-list-btn {
  width: 100px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
}

/* Utility widths - inline via Tailwind op skeleton-line */
.w-1\/2 { width: 50% !important; }
.w-4\/5 { width: 80% !important; }
.w-1\/3 { width: 33% !important; }
.w-3\/5 { width: 60% !important; }
.w-2\/5 { width: 40% !important; }
.flex-1 { flex: 1 !important; }

/* ═══════════════════════════════════════════════════════════════════
   LANDING PAGE - parent categorie met subcategorieën
   ═══════════════════════════════════════════════════════════════════ */

/* Intro / trust-strip - hairline, gedempt */
.landing-intro {
  background: #fff;
  border-bottom: 1px solid #f1f2f4;
  padding: 1rem 0;
}
.landing-trust-strip {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem 2rem;
  font-size: .8125rem;
  font-weight: 500;
  color: #777;
}
.landing-trust-strip span { display: flex; align-items: center; gap: .5rem; }
.landing-trust-strip i { color: #c9a900; font-size: .9rem; }

/* Subcategorie-grid */
.landing-grid-section { background: #fff; padding: 3.5rem 0 4.5rem; }
.landing-grid-head { margin-bottom: 2.25rem; }
.landing-grid-title {
  font-size: clamp(1.375rem, 2.6vw, 1.9rem);
  font-weight: 800; color: #111; letter-spacing: -.4px;
  margin: 0 0 .35rem; text-wrap: balance;
}
.landing-grid-sub { font-size: .95rem; color: #777; margin: 0; }

.landing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem 2rem;
}
@media (max-width: 991px) { .landing-grid { grid-template-columns: repeat(2, 1fr); gap: 2rem 1.5rem; } }
@media (max-width: 600px) { .landing-grid { grid-template-columns: 1fr; } }

/* Kaart - borderless, beeld-voorop */
.landing-card {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
}
.landing-card-media {
  position: relative;
  aspect-ratio: 4/3;
  border-radius: 14px;
  overflow: hidden;
  background: #f4f5f7;
  margin-bottom: 1rem;
}
.landing-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform .5s cubic-bezier(.22,1,.36,1);
}
.landing-card:hover .landing-card-img { transform: scale(1.04); }

.landing-card-icon {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
}
.landing-card-icon i { font-size: 2.75rem; color: #e6c200; }

/* Body */
.landing-card-body { display: flex; flex-direction: column; }
.landing-card-meta {
  font-size: .7rem; font-weight: 700; letter-spacing: .6px; text-transform: uppercase;
  color: #aaa; margin-bottom: .45rem;
}
.landing-card-title {
  font-size: 1.125rem; font-weight: 800; color: #111;
  letter-spacing: -.2px; line-height: 1.25; margin: 0 0 .4rem;
  transition: color .15s;
}
.landing-card:hover .landing-card-title { color: #8a6d00; }
.landing-card-desc {
  font-size: .875rem; color: #6b7280; line-height: 1.6; margin: 0 0 .9rem;
}
.landing-card-cta {
  display: inline-flex; align-items: center; gap: .4rem;
  font-size: .8125rem; font-weight: 700; color: #111;
  transition: gap .2s;
}
.landing-card-cta i { color: #c9a900; transition: transform .2s; }
.landing-card:hover .landing-card-cta { gap: .65rem; }
.landing-card:hover .landing-card-cta i { transform: translateX(2px); }

/* SEO-blok (landing) - rustig, hairline */
.landing-seo-section {
  background: #fafafa;
  border-top: 1px solid #f1f2f4;
  padding: 3.5rem 0;
}
.landing-seo-grid {
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 3.5rem;
}
@media (max-width: 768px) { .landing-seo-grid { grid-template-columns: 1fr; gap: 2rem; } }

.landing-seo-title {
  font-size: 1.375rem;
  font-weight: 800;
  color: #111;
  letter-spacing: -.3px;
  margin-bottom: 1rem;
}
.landing-seo-body {
  font-size: .9375rem;
  color: #555;
  line-height: 1.85;
  max-width: 65ch;
}

/* Navigatiepaneel rechts - hairline-lijst i.p.v. box */
.landing-seo-nav {
  align-self: flex-start;
  position: sticky;
  top: 100px;
}
.landing-seo-nav-title {
  font-size: .7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: #aaa;
  margin-bottom: .5rem;
}
.landing-seo-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.landing-seo-link {
  display: flex;
  align-items: center;
  gap: .625rem;
  text-decoration: none;
  color: #444;
  font-size: .9rem;
  font-weight: 500;
  padding: .625rem 0;
  border-bottom: 1px solid #eef0f2;
  transition: color .15s, padding-left .15s;
}
.landing-seo-link:last-child { border-bottom: none; }
.landing-seo-link:hover { color: #8a6d00; padding-left: .25rem; }
.landing-seo-link i { font-size: .85rem; color: #c9a900; flex-shrink: 0; }
.landing-seo-link-count {
  margin-left: auto;
  font-size: .72rem;
  color: #bbb;
  font-weight: 600;
}

/* ─── Categorie-FAQ sectie ─────────────────────────────────────────────────── */
.cat-faq-section {
  background: #fff;
  padding: 4rem 0 3.5rem;
  border-top: 1px solid #f0f0f0;
}

.cat-faq-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2.25rem;
}

.cat-faq-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: #FFD500;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  shrink: 0;
  flex-shrink: 0;
}
.cat-faq-icon i {
  font-size: 1.1rem;
  color: #111;
}

.cat-faq-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 .25rem;
  line-height: 1.3;
}

.cat-faq-sub {
  font-size: .875rem;
  color: #888;
  margin: 0;
}

.cat-faq-group-label {
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  color: #aaa;
  margin: 1.5rem 0 .5rem;
  padding: 0 .25rem;
}

.cat-faq-list {
  background: #f9fafb;
  border: 1px solid #e8eaed;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: .75rem;
}

.cat-faq-item {
  border-bottom: 1px solid #f0f2f5;
}
.cat-faq-item:last-child {
  border-bottom: none;
}

.cat-faq-q {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.125rem 1.25rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: .9rem;
  font-weight: 600;
  color: #222;
  transition: background .15s ease;
}
.cat-faq-q:hover {
  background: #f4f5f7;
}
.cat-faq-item.open .cat-faq-q {
  background: #fffbeb;
  color: #111;
}

.cat-faq-chevron {
  width: 1.6rem;
  height: 1.6rem;
  background: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform .2s ease, background .15s ease;
}
.cat-faq-item.open .cat-faq-chevron {
  transform: rotate(180deg);
  background: #FFD500;
}
.cat-faq-chevron i {
  font-size: .65rem;
  font-weight: 900;
  color: #555;
}
.cat-faq-item.open .cat-faq-chevron i {
  color: #111;
}

.cat-faq-a {
  padding: .25rem 1.25rem 1.25rem;
  font-size: .875rem;
  line-height: 1.75;
  color: #555;
  border-top: 1px solid #eef0f3;
}
.cat-faq-a :deep(p)   { margin-bottom: .6rem; }
.cat-faq-a :deep(ul)  { list-style: disc; padding-left: 1.25rem; margin-bottom: .6rem; }
.cat-faq-a :deep(ol)  { list-style: decimal; padding-left: 1.25rem; margin-bottom: .6rem; }
.cat-faq-a :deep(a)   { color: #b89b00; font-weight: 600; }
.cat-faq-a :deep(strong) { color: #111; }

.cat-faq-more {
  margin-top: 1.75rem;
  text-align: center;
  font-size: .875rem;
  color: #888;
}
.cat-faq-more-link {
  color: #b89b00;
  font-weight: 600;
  text-decoration: none;
}
.cat-faq-more-link:hover {
  text-decoration: underline;
}

/* ─── Keuzehulp-instap ─────────────────────────────────────────────────────── */
.keuzehulp-cta {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.25rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  text-decoration: none;
  transition: border-color .18s ease, box-shadow .18s ease;
}
.keuzehulp-cta:hover {
  border-color: #FFD500;
  box-shadow: 0 4px 16px rgba(0,0,0,.06);
}
.keuzehulp-cta .kh-icon {
  flex-shrink: 0;
  width: 38px; height: 38px;
  border-radius: 50%;
  background: #FFF6CC;
  display: flex; align-items: center; justify-content: center;
  color: #b8860b; font-size: 1.05rem;
}
.keuzehulp-cta .kh-text { flex: 1; min-width: 0; line-height: 1.3; }
.keuzehulp-cta .kh-text strong { display: block; font-size: 0.9rem; color: #111; }
.keuzehulp-cta .kh-text span { display: block; font-size: 0.8rem; color: #777; }
.keuzehulp-cta .kh-arrow {
  flex-shrink: 0;
  color: #b8860b; font-weight: 600; font-size: 0.85rem;
  display: inline-flex; align-items: center; gap: 0.25rem;
}
</style>